import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { useState } from "react";

import { Button } from "components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "components/ui/sheet";

import { QrCode } from "./QrCode";
import { connectUrl, formatCode } from "./connect-code";
import { useMyConnectCode } from "./use-my-connect-code";
import { useWakeLock } from "./use-wake-lock";

/**
 * "Show my code": the person's QR, full screen, with the code in letters for
 * reading aloud or typing (TNG-93). The QR is a link into the app naming the
 * event, so a phone's own camera app opens the connect page too.
 */
export function MyCodeSheet({
  uid,
  eventSlug,
  eventTitle,
  open,
  onOpenChange,
}: {
  uid: string;
  eventSlug: string;
  eventTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="flex h-[100dvh] flex-col items-center overflow-y-auto sm:h-auto sm:max-h-[100dvh]"
        data-testid="my-code-sheet"
      >
        {open && (
          <RetryableMyCode
            uid={uid}
            eventSlug={eventSlug}
            eventTitle={eventTitle}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

/** "Try again" remounts the code view, which reads and issues afresh. */
function RetryableMyCode(props: {
  uid: string;
  eventSlug: string;
  eventTitle: string;
}) {
  const [attempt, setAttempt] = useState(0);
  return (
    <MyCode
      key={attempt}
      {...props}
      onRetry={() => setAttempt((value) => value + 1)}
    />
  );
}

function MyCode({
  uid,
  eventSlug,
  eventTitle,
  onRetry,
}: {
  uid: string;
  eventSlug: string;
  eventTitle: string;
  onRetry: () => void;
}) {
  const { state, newCode, rotateError } = useMyConnectCode(uid, true);
  const [confirming, setConfirming] = useState(false);
  useWakeLock(true);

  return (
    <div className="flex w-full max-w-md flex-col items-center gap-5 pt-6 text-center">
      <SheetHeader className="items-center text-center">
        <SheetTitle>My connect code</SheetTitle>
        <SheetDescription>
          Let someone at {eventTitle} scan this, or read them the code. You both
          get each other&apos;s card.
        </SheetDescription>
      </SheetHeader>

      {state.status === "loading" && (
        <div
          className="aspect-square w-full max-w-[320px] animate-pulse rounded-lg bg-muted"
          aria-hidden
        />
      )}

      {state.status === "issuing" && (
        <div
          className="flex aspect-square w-full max-w-[320px] flex-col items-center justify-center gap-3 rounded-lg bg-muted p-6 text-sm text-muted-foreground"
          role="status"
          data-testid="my-code-issuing"
        >
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
          {state.queued
            ? "Your code appears here once you are back online."
            : "Making your code…"}
        </div>
      )}

      {state.status === "error" && (
        <div className="space-y-3" data-testid="my-code-error">
          <p
            className="flex items-center gap-2 text-sm text-foreground"
            role="alert"
          >
            <AlertCircle className="h-4 w-4 shrink-0 text-destructive-text dark:text-hb-red-text" />
            Your code could not be loaded. ({state.error})
          </p>
          <Button type="button" variant="outline" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}

      {state.status === "ready" && (
        <>
          <QrCode
            value={connectUrl(window.location.origin, state.code, eventSlug)}
            label={`QR code for your connect code ${formatCode(state.code)}`}
            className="aspect-square w-full max-w-[320px] rounded-lg border border-border"
          />
          <p
            className="font-mono text-4xl font-semibold tracking-[0.15em] text-foreground tabular-nums"
            data-testid="my-connect-code"
            aria-label={`Your code: ${state.code.split("").join(" ")}`}
          >
            {formatCode(state.code)}
          </p>

          {confirming ? (
            <div
              className="w-full space-y-3 rounded-lg border border-border bg-card p-4 text-left text-card-foreground"
              data-testid="new-code-confirm"
            >
              <p className="text-sm text-foreground">
                Your current code stops working. Everyone already in your
                connections stays there.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="destructive"
                  className="dark:border-hb-red-text dark:text-foreground"
                  type="button"
                  onClick={() => {
                    setConfirming(false);
                    newCode();
                  }}
                  data-testid="new-code-confirm-button"
                >
                  Make a new code
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirming(false)}
                >
                  Keep this code
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirming(true)}
              disabled={state.rotating}
              data-testid="new-code"
            >
              <RefreshCw
                className={state.rotating ? "animate-spin" : undefined}
              />
              {state.rotating ? "Making a new code…" : "New code"}
            </Button>
          )}
          {rotateError && (
            <p
              className="flex items-start gap-2 text-sm text-foreground"
              role="alert"
            >
              <AlertCircle
                className="mt-0.5 h-4 w-4 shrink-0 text-destructive-text dark:text-hb-red-text"
                aria-hidden
              />
              {rotateError}
            </p>
          )}
        </>
      )}
    </div>
  );
}
